import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  MdAdd,
  MdAssignmentInd,
  MdCheckCircle,
  MdClose,
  MdDone,
  MdForum,
  MdHistory,
  MdLock,
  MdLockOpen,
  MdOutlineSupportAgent,
  MdPerson,
  MdPriorityHigh,
  MdRefresh,
  MdSearch,
  MdSend,
  MdTag,
} from "react-icons/md";

const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  page: "#f5f6f8",
  card: "#ffffff",
  ok: "#16a34a",
  warn: "#b45309",
  danger: "#b91c1c",
  info: "#2563eb",
};

const STATUS_META = {
  open: { label: "Open", bg: "#ecfdf5", fg: COLORS.ok },
  pending: { label: "Pending", bg: "#fff7ed", fg: COLORS.warn },
  resolved: { label: "Resolved", bg: "#eff6ff", fg: COLORS.info },
  closed: { label: "Closed", bg: "#f1f5f9", fg: COLORS.subtext },
};

const PRIORITY_META = {
  low: { label: "Low", bg: "#f1f5f9", fg: COLORS.subtext },
  normal: { label: "Normal", bg: "#eff6ff", fg: COLORS.info },
  high: { label: "High", bg: "#fff7ed", fg: COLORS.warn },
  urgent: { label: "Urgent", bg: "#fef2f2", fg: COLORS.danger },
};

export default function ChatSupport() {
  const [tickets, setTickets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [roleToAdd, setRoleToAdd] = useState("");
  const [assignRoleFilter, setAssignRoleFilter] = useState("");
  const [agentToAssign, setAgentToAssign] = useState("");
  const [assignmentComment, setAssignmentComment] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState("");
  const [activeSupportTab, setActiveSupportTab] = useState("chat");

  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const employee = useMemo(() => safeEmployee(), []);
  const actor = useMemo(() => actorFromEmployee(employee), [employee]);

  const myRole = String(employee?.role || "");
  const myUid = actor.id;

  useEffect(() => {
    const qTickets = query(collection(db, "chat_support"), orderBy("updated_at", "desc"));
    const unsubTickets = onSnapshot(qTickets, (snap) => {
      const list = snap.docs.map((d) => normalizeTicket(d.id, d.data()));
      setTickets(list);
      setSelectedTicketId((current) => {
        if (current && list.some((ticket) => ticket.id === current)) return current;
        return list[0]?.id || null;
      });
    });

    const unsubEmployees = onSnapshot(collection(db, "employees"), (snap) => {
      const list = snap.docs
        .map((d) => ({ docId: d.id, ...d.data() }))
        .sort((a, b) => employeeLabel(a).localeCompare(employeeLabel(b)));
      setEmployees(list);
    });

    return () => {
      unsubTickets();
      unsubEmployees();
    };
  }, []);

  useEffect(() => {
    if (!selectedTicketId) {
      setMessages([]);
      return undefined;
    }

    const qMsgs = query(
      collection(db, `chat_support/${selectedTicketId}/messages`),
      orderBy("timestamp")
    );
    const unsubMsgs = onSnapshot(qMsgs, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: "smooth" }), 0);
    });

    return () => {
      unsubMsgs();
    };
  }, [selectedTicketId]);

  const roles = useMemo(() => {
    const set = new Set(["support_staff", "admin"]);
    employees.forEach((emp) => {
      if (emp.role) set.add(String(emp.role));
    });
    tickets.forEach((ticket) => {
      (ticket.assigned_roles || []).forEach((role) => set.add(String(role)));
    });
    return [...set].sort();
  }, [employees, tickets]);

  const assignableEmployees = useMemo(() => {
    return employees.filter((emp) => !assignRoleFilter || String(emp.role || "") === assignRoleFilter);
  }, [assignRoleFilter, employees]);

  const activeTicket = tickets.find((ticket) => ticket.id === selectedTicketId);

  const filteredTickets = useMemo(() => {
    const qText = search.trim().toLowerCase();
    return tickets.filter((ticket) => {
      const status = String(ticket.status || "open");
      if (statusFilter === "active" && status === "closed") return false;
      if (statusFilter !== "active" && statusFilter !== "all" && status !== statusFilter) return false;
      if (priorityFilter !== "all" && ticket.priority !== priorityFilter) return false;
      if (assignmentFilter === "mine" && ticket.assigned_agent_uid !== myUid) return false;
      if (
        assignmentFilter === "role" &&
        myRole &&
        !(ticket.assigned_roles || []).includes(myRole)
      ) {
        return false;
      }
      if (
        assignmentFilter === "unassigned" &&
        ((ticket.assigned_roles || []).length > 0 || ticket.assigned_agent_uid)
      ) {
        return false;
      }
      if (!qText) return true;

      const hay = [
        ticket.ticket_no,
        ticket.subject,
        ticket.category,
        ticket.created_by_user_uid,
        ticket.user_type,
        ticket.assigned_agent_name,
        (ticket.assigned_roles || []).join(" "),
        ticket.last_message_preview,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(qText);
    });
  }, [assignmentFilter, myRole, myUid, priorityFilter, search, statusFilter, tickets]);

  const stats = useMemo(() => {
    return tickets.reduce(
      (acc, ticket) => {
        const status = ticket.status || "open";
        acc.total += 1;
        acc[status] = (acc[status] || 0) + 1;
        if (!ticket.assigned_agent_uid && (ticket.assigned_roles || []).length === 0) acc.unassigned += 1;
        return acc;
      },
      { total: 0, open: 0, pending: 0, resolved: 0, closed: 0, unassigned: 0 }
    );
  }, [tickets]);

  const tabTickets = useMemo(() => {
    if (activeSupportTab === "open") {
      return filteredTickets.filter((ticket) => String(ticket.status || "open") !== "closed");
    }
    if (activeSupportTab === "closed") {
      return filteredTickets.filter((ticket) => String(ticket.status || "open") === "closed");
    }
    return filteredTickets;
  }, [activeSupportTab, filteredTickets]);

  if (!employee?.role) {
    return <div style={denied}>Session invalid. Please log in again.</div>;
  }

  return (
    <div style={page}>
      <section style={summaryBar}>
        <div style={summaryTitleWrap}>
          <div style={summaryIcon}>
            <MdOutlineSupportAgent />
          </div>
          <div>
            <h2 style={summaryTitle}>Support Tickets</h2>
          <p style={summarySub}>Open, assign with context, resolve, and continue live chat.</p>
          </div>
        </div>
        <div style={statsWrap}>
          <Stat label="Open" value={stats.open} tone="ok" />
          <Stat label="Pending" value={stats.pending} tone="warn" />
          <Stat label="Resolved" value={stats.resolved} tone="info" />
          <Stat label="Unassigned" value={stats.unassigned} tone="danger" />
          <button type="button" style={auditPageBtn} onClick={() => navigate("/panel/chat-support-audit")}>
            <MdHistory /> Chat Audit
          </button>
        </div>
      </section>

      <div style={supportTabs}>
        {[
          { key: "chat", label: "Chat", count: stats.open + stats.pending + stats.resolved, icon: <MdForum /> },
          { key: "open", label: "Open Tickets", count: stats.open + stats.pending + stats.resolved, icon: <MdLockOpen /> },
          { key: "closed", label: "Closed Tickets", count: stats.closed, icon: <MdLock /> },
        ].map((tab) => {
          const active = activeSupportTab === tab.key;
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setActiveSupportTab(tab.key);
                if (tab.key === "open") setStatusFilter("active");
                if (tab.key === "closed") setStatusFilter("closed");
              }}
              style={active ? supportTabActive : supportTab}
            >
              {tab.icon}
              <span>{tab.label}</span>
              <b>{tab.count}</b>
            </button>
          );
        })}
      </div>

      <div style={workspace}>
        <section style={leftPane}>
          <div style={toolbar}>
            <div style={searchBox}>
              <MdSearch />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search ticket, user, role..."
                style={searchInput}
              />
            </div>
            <div style={filterGrid}>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={select}>
                <option value="active">Active tickets</option>
                <option value="all">All statuses</option>
                <option value="open">Open</option>
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
              <select
                value={assignmentFilter}
                onChange={(e) => setAssignmentFilter(e.target.value)}
                style={select}
              >
                <option value="all">All assignment</option>
                <option value="mine">Assigned to me</option>
                <option value="role">My role</option>
                <option value="unassigned">Unassigned</option>
              </select>
              <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} style={select}>
                <option value="all">All priority</option>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div style={ticketList}>
            {tabTickets.length === 0 ? (
              <div style={emptyNote}>No tickets match these filters.</div>
            ) : (
              tabTickets.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => setSelectedTicketId(ticket.id)}
                  style={{
                    ...ticketItem,
                    borderColor: ticket.id === selectedTicketId ? COLORS.primary : COLORS.border,
                    background: ticket.id === selectedTicketId ? "#fff7fb" : "#fff",
                  }}
                >
                  <div style={ticketTopLine}>
                    <span style={ticketNumber}>{ticket.ticket_no || shortId(ticket.id)}</span>
                    <StatusBadge status={ticket.status} />
                  </div>
                  <div style={ticketSubject}>{ticket.subject || "Live support request"}</div>
                  <div style={ticketUserLine}>
                    <MdPerson />
                    <span>{ticket.created_by_user_uid || "Unknown user"}</span>
                  </div>
                  <div style={ticketMetaLine}>
                    <PriorityBadge priority={ticket.priority} />
                    <span>{ticket.category || "general"}</span>
                    <span>{timeAgo(ticket.updated_at)}</span>
                  </div>
                  <div style={roleWrapSmall}>
                    {(ticket.assigned_roles || []).map((role) => (
                      <span key={role} style={roleChipSmall}>
                        <MdTag /> {role}
                      </span>
                    ))}
                    {ticket.assigned_agent_name ? (
                      <span style={agentChipSmall}>
                        <MdAssignmentInd /> {ticket.assigned_agent_name}
                      </span>
                    ) : null}
                    {!ticket.assigned_agent_name && (ticket.assigned_roles || []).length === 0 ? (
                      <span style={ghostChip}>Unassigned</span>
                    ) : null}
                  </div>
                </button>
              ))
            )}
          </div>
        </section>

        <main style={mainPane}>
          {!activeTicket ? (
            <div style={emptyPane}>
              <MdForum size={42} />
              <strong>Select a support ticket</strong>
              <span>Live messages, assignment, status, and audit will show here.</span>
            </div>
          ) : (
            <>
              <header style={ticketHeader}>
                <div>
                  <div style={headerTicketLine}>
                    <span style={ticketNumberLarge}>{activeTicket.ticket_no || shortId(activeTicket.id)}</span>
                    <StatusBadge status={activeTicket.status} />
                    <PriorityBadge priority={activeTicket.priority} />
                  </div>
                  <h3 style={headerSubject}>{activeTicket.subject || "Live support request"}</h3>
                  <div style={headerMeta}>
                    <span>{activeTicket.user_type || "user"}</span>
                    <span>{activeTicket.created_by_user_uid || "Unknown user"}</span>
                    <span>Created {timeAgo(activeTicket.created_at)}</span>
                  </div>
                </div>
                <div style={quickActions}>
                  <button type="button" style={ghostBtn} onClick={() => setStatus(activeTicket, "open")}>
                    <MdLockOpen /> Open
                  </button>
                  <button type="button" style={ghostBtn} onClick={() => setStatus(activeTicket, "pending")}>
                    <MdRefresh /> Pending
                  </button>
                  <button type="button" style={ghostBtn} onClick={() => setStatus(activeTicket, "resolved")}>
                    <MdDone /> Resolve
                  </button>
                  <button type="button" style={dangerBtn} onClick={() => setStatus(activeTicket, "closed")}>
                    <MdLock /> Close
                  </button>
                </div>
              </header>

              <div style={contentGrid}>
                <section style={chatPane}>
                  <div style={messagesWrap}>
                    {messages.length === 0 ? (
                      <div style={emptyNote}>No messages yet. Send the first reply to this ticket.</div>
                    ) : (
                      messages.map((msg) => {
                        const isAgent = msg.sender_role === "agent";
                        return (
                          <div
                            key={msg.id}
                            style={{
                              ...messageRow,
                              justifyContent: isAgent ? "flex-end" : "flex-start",
                            }}
                          >
                            <div style={{ maxWidth: "72%", textAlign: isAgent ? "right" : "left" }}>
                              <div style={messageAuthor}>
                                {displaySender(msg, isAgent)}
                              </div>
                              <div style={isAgent ? agentBubble : userBubble}>{msg.content}</div>
                              <div style={messageTime}>{timeAgo(msg.timestamp)}</div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={scrollRef} />
                  </div>

                  <div style={composer}>
                    <input
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          sendMessage(activeTicket);
                        }
                      }}
                      disabled={activeTicket.status === "closed"}
                      placeholder={activeTicket.status === "closed" ? "Reopen ticket to reply" : "Type reply..."}
                      style={composerInput}
                    />
                    <button
                      type="button"
                      onClick={() => sendMessage(activeTicket)}
                      disabled={!message.trim() || activeTicket.status === "closed" || busy === "send"}
                      style={sendBtn}
                    >
                      <MdSend /> Send
                    </button>
                  </div>
                </section>

                <aside style={sidePane}>
                  <Panel title="Assignment" icon={<MdAssignmentInd />}>
                    <label style={label}>Route by employee role</label>
                    <select
                      value={assignRoleFilter}
                      onChange={(e) => {
                        setAssignRoleFilter(e.target.value);
                        setAgentToAssign("");
                      }}
                      style={selectFull}
                    >
                      <option value="">All roles</option>
                      {roles.map((role) => (
                        <option key={role} value={role}>
                          {formatRole(role)}
                        </option>
                      ))}
                    </select>

                    <label style={label}>Assign employee</label>
                    <div style={inlineControls}>
                      <select
                        value={agentToAssign}
                        onChange={(e) => setAgentToAssign(e.target.value)}
                        style={select}
                      >
                        <option value="">Select employee</option>
                        {assignableEmployees.map((emp) => (
                          <option key={emp.docId} value={emp.docId}>
                            {employeeLabel(emp)} ({formatRole(emp.role || "role")})
                          </option>
                        ))}
                      </select>
                      <button type="button" style={iconBtn} onClick={() => assignEmployee(activeTicket)}>
                        <MdCheckCircle />
                      </button>
                    </div>
                    <label style={label}>Assignment comment</label>
                    <textarea
                      value={assignmentComment}
                      onChange={(e) => setAssignmentComment(e.target.value)}
                      placeholder="Example: Profile issue, account verification, wallet issue..."
                      style={commentBox}
                    />
                    <button type="button" style={fullGhostBtn} onClick={() => assignToMe(activeTicket)}>
                      <MdAssignmentInd /> Take ticket
                    </button>

                    <label style={label}>Assign role</label>
                    <div style={inlineControls}>
                      <select value={roleToAdd} onChange={(e) => setRoleToAdd(e.target.value)} style={select}>
                        <option value="">Select role</option>
                        {roles
                          .filter((role) => !(activeTicket.assigned_roles || []).includes(role))
                          .map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                      </select>
                      <button type="button" style={iconBtn} onClick={() => addRole(activeTicket)}>
                        <MdAdd />
                      </button>
                    </div>
                    <div style={roleWrap}>
                      {(activeTicket.assigned_roles || []).map((role) => (
                        <span key={role} style={roleChip}>
                          <MdTag /> {role}
                          <button type="button" style={chipClose} onClick={() => removeRole(activeTicket, role)}>
                            <MdClose />
                          </button>
                        </span>
                      ))}
                      {(activeTicket.assigned_roles || []).length === 0 ? (
                        <span style={ghostChip}>No role assigned</span>
                      ) : null}
                    </div>
                  </Panel>

                  <Panel title="Ticket Details" icon={<MdPriorityHigh />}>
                    <label style={label}>Priority</label>
                    <select
                      value={activeTicket.priority}
                      onChange={(e) => setPriority(activeTicket, e.target.value)}
                      style={selectFull}
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                    <div style={detailList}>
                      <Detail label="Category" value={activeTicket.category || "general"} />
                      <Detail label="Agent" value={activeTicket.assigned_agent_name || "Unassigned"} />
                      <Detail label="Assignment note" value={activeTicket.assignment_comment || "-"} />
                      <Detail label="Last message" value={activeTicket.last_message_preview || "-"} />
                      <Detail label="Closed at" value={formatDate(activeTicket.closed_at) || "-"} />
                    </div>
                  </Panel>

                </aside>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );

  async function writeAudit(ticketId, action, details = "") {
    await addDoc(collection(db, `chat_support/${ticketId}/audit`), {
      action,
      details,
      actor,
      created_at: serverTimestamp(),
    });
  }

  async function updateTicket(ticket, patch, action, details) {
    setBusy(action);
    try {
      await updateDoc(doc(db, "chat_support", ticket.id), {
        ...patch,
        updated_at: serverTimestamp(),
        last_admin_activity_at: serverTimestamp(),
      });
      await writeAudit(ticket.id, action, details);
    } catch (error) {
      console.error(error);
    } finally {
      setBusy("");
    }
  }

  async function setStatus(ticket, nextStatus) {
    if (!ticket || ticket.status === nextStatus) return;
    const patch = {
      status: nextStatus,
      status_label: STATUS_META[nextStatus]?.label || nextStatus,
    };
    if (nextStatus === "closed") {
      patch.closed_at = serverTimestamp();
      patch.closed_by = actor;
    } else {
      patch.closed_at = null;
      patch.closed_by = null;
    }
    await updateTicket(ticket, patch, `status_${nextStatus}`, `Status changed to ${nextStatus}`);
  }

  async function setPriority(ticket, priority) {
    await updateTicket(ticket, { priority }, "priority_changed", `Priority changed to ${priority}`);
  }

  async function assignToMe(ticket) {
    const comment = assignmentComment.trim();
    await updateTicket(
      ticket,
      {
        assigned_agent_uid: myUid || null,
        assigned_agent_name: actor.name,
        assigned_agent_role: actor.role,
        assignment_comment: comment || null,
        assigned_at: serverTimestamp(),
        assigned_by: actor,
      },
      "assigned_agent",
      `Assigned to ${actor.name}${comment ? ` | Comment: ${comment}` : ""}`
    );
    setAssignmentComment("");
  }

  async function assignEmployee(ticket) {
    if (!agentToAssign) return;
    const emp = employees.find((item) => item.docId === agentToAssign);
    if (!emp) return;
    const name = employeeLabel(emp);
    const comment = assignmentComment.trim();
    await updateTicket(
      ticket,
      {
        assigned_agent_uid: emp.uid || emp.id || emp.docId,
        assigned_agent_name: name,
        assigned_agent_role: emp.role || "",
        assigned_employee_doc_id: emp.docId,
        assignment_comment: comment || null,
        assigned_at: serverTimestamp(),
        assigned_by: actor,
      },
      "assigned_agent",
      `Assigned to ${name}${emp.role ? ` (${formatRole(emp.role)})` : ""}${comment ? ` | Comment: ${comment}` : ""}`
    );
    setAgentToAssign("");
    setAssignmentComment("");
  }

  async function addRole(ticket) {
    if (!roleToAdd) return;
    await updateTicket(
      ticket,
      { assigned_roles: arrayUnion(roleToAdd) },
      "role_added",
      `Role added: ${roleToAdd}`
    );
    setRoleToAdd("");
  }

  async function removeRole(ticket, role) {
    await updateTicket(ticket, { assigned_roles: arrayRemove(role) }, "role_removed", `Role removed: ${role}`);
  }

  async function sendMessage(ticket) {
    const text = message.trim();
    if (!ticket || !text || ticket.status === "closed") return;
    setBusy("send");
    try {
      await addDoc(collection(db, `chat_support/${ticket.id}/messages`), {
        sender_uid: myUid || null,
        sender_name: actor.name,
        sender_role: "agent",
        sender_role_name: actor.role || null,
        type: "text",
        content: text,
        timestamp: serverTimestamp(),
      });
      await updateDoc(doc(db, "chat_support", ticket.id), {
        status: ticket.status === "resolved" ? "open" : ticket.status || "open",
        updated_at: serverTimestamp(),
        last_message_preview: text.slice(0, 140),
        last_message_at: serverTimestamp(),
        last_message_by: "agent",
        assigned_agent_uid: ticket.assigned_agent_uid || myUid || null,
        assigned_agent_name: ticket.assigned_agent_name || actor.name,
        assigned_agent_role: ticket.assigned_agent_role || actor.role,
      });
      await writeAudit(ticket.id, "agent_replied", "Agent sent a chat reply");
      setMessage("");
      setTimeout(() => scrollRef.current?.scrollIntoView({ behavior: "smooth" }), 0);
    } catch (error) {
      console.error(error);
    } finally {
      setBusy("");
    }
  }
}

function Panel({ title, icon, children }) {
  return (
    <section style={panel}>
      <div style={panelTitle}>
        <span style={panelIcon}>{icon}</span>
        <strong>{title}</strong>
      </div>
      <div style={panelBody}>{children}</div>
    </section>
  );
}

function Detail({ label, value }) {
  return (
    <div style={detailRow}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const map = {
    ok: { bg: "#ecfdf5", fg: COLORS.ok },
    warn: { bg: "#fff7ed", fg: COLORS.warn },
    info: { bg: "#eff6ff", fg: COLORS.info },
    danger: { bg: "#fef2f2", fg: COLORS.danger },
  };
  const colors = map[tone] || map.info;
  return (
    <div style={{ ...statBox, background: colors.bg, color: colors.fg }}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status || "open"] || STATUS_META.open;
  return <span style={{ ...badge, background: meta.bg, color: meta.fg }}>{meta.label}</span>;
}

function PriorityBadge({ priority }) {
  const meta = PRIORITY_META[priority || "normal"] || PRIORITY_META.normal;
  return <span style={{ ...badge, background: meta.bg, color: meta.fg }}>{meta.label}</span>;
}

function normalizeTicket(id, data = {}) {
  return {
    id,
    assigned_roles: Array.isArray(data.assigned_roles) ? data.assigned_roles : [],
    category: String(data.category || "general"),
    priority: String(data.priority || "normal"),
    status: String(data.status || "open"),
    subject: String(data.subject || "Live support request"),
    ticket_no: data.ticket_no || data.ticketNo || "",
    ...data,
  };
}

function safeEmployee() {
  try {
    const raw = localStorage.getItem("employee");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function actorFromEmployee(employee = {}) {
  return {
    id: employee.uid || employee.id || employee.docId || employee.mobile || "",
    name: employee.name || employee.fullName || employee.full_name || employee.mobile || "Agent",
    role: employee.role || "",
    mobile: employee.mobile || "",
  };
}

function displaySender(message, isAgent) {
  if (!isAgent) return "User";
  const role = message.sender_role_name ? ` (${message.sender_role_name})` : "";
  return `${message.sender_name || "Agent"}${role}`;
}

function toDate(value) {
  if (!value) return null;
  if (value.seconds) return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function timeAgo(value) {
  const date = toDate(value);
  if (!date) return "-";
  const diff = Date.now() - date.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  return `${day}d`;
}

function formatDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleString() : "";
}

function shortId(id = "") {
  return `TKT-${String(id).slice(0, 6).toUpperCase()}`;
}

function employeeLabel(employee = {}) {
  return employee.name || employee.fullName || employee.full_name || employee.mobile || employee.docId || "Employee";
}

function formatRole(role = "") {
  return String(role || "role")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const page = {
  display: "grid",
  gap: 12,
  minHeight: "calc(100vh - 145px)",
  fontFamily: "Inter, system-ui, sans-serif",
};
const summaryBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: 14,
  borderRadius: 12,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
};
const summaryTitleWrap = { display: "flex", alignItems: "center", gap: 12 };
const summaryIcon = {
  width: 42,
  height: 42,
  borderRadius: 10,
  display: "grid",
  placeItems: "center",
  color: "#fff",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};
const summaryTitle = { margin: 0, color: COLORS.text, fontSize: 22, lineHeight: 1.1 };
const summarySub = { margin: "4px 0 0", color: COLORS.subtext, fontSize: 13 };
const statsWrap = { display: "flex", gap: 8, flexWrap: "wrap" };
const supportTabs = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 10,
};
const supportTab = {
  minHeight: 48,
  borderRadius: 12,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  cursor: "pointer",
  fontWeight: 900,
};
const supportTabActive = {
  ...supportTab,
  border: "1px solid transparent",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  boxShadow: "0 12px 24px rgba(242,12,83,.18)",
};
const statBox = {
  display: "grid",
  gap: 2,
  minWidth: 92,
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid rgba(15,23,42,0.08)",
};
const auditPageBtn = {
  minHeight: 44,
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  padding: "0 12px",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 900,
  cursor: "pointer",
};
const workspace = {
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 12,
  minHeight: 0,
};
const leftPane = {
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  overflow: "hidden",
  display: "grid",
  minHeight: 0,
};
const toolbar = { padding: 12, display: "grid", gap: 10, borderBottom: `1px solid ${COLORS.border}` };
const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
};
const searchInput = { flex: 1, border: "none", outline: "none", minWidth: 0 };
const filterGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 8 };
const select = {
  minWidth: 0,
  padding: "9px 10px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
};
const selectFull = { ...select, width: "100%" };
const commentBox = {
  width: "100%",
  minHeight: 84,
  padding: 10,
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  outline: "none",
  resize: "vertical",
  color: COLORS.text,
};
const ticketList = { padding: 10, display: "grid", gap: 10 };
const ticketItem = {
  width: "100%",
  textAlign: "left",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  padding: 12,
  cursor: "pointer",
  background: "#fff",
};
const ticketTopLine = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 };
const ticketNumber = { color: COLORS.primary, fontWeight: 900, fontSize: 12 };
const ticketNumberLarge = { color: COLORS.primary, fontWeight: 900 };
const ticketSubject = { marginTop: 8, fontWeight: 900, color: COLORS.text };
const ticketUserLine = {
  marginTop: 6,
  display: "flex",
  alignItems: "center",
  gap: 6,
  color: COLORS.subtext,
  fontSize: 12,
};
const ticketMetaLine = {
  marginTop: 8,
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
  color: COLORS.subtext,
  fontSize: 12,
};
const roleWrapSmall = { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 };
const badge = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 900,
};
const mainPane = {
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  overflow: "hidden",
  display: "grid",
  minWidth: 0,
};
const emptyPane = {
  height: "100%",
  minHeight: 420,
  display: "grid",
  placeItems: "center",
  alignContent: "center",
  gap: 8,
  color: COLORS.subtext,
};
const ticketHeader = {
  padding: 14,
  borderBottom: `1px solid ${COLORS.border}`,
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};
const headerTicketLine = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const headerSubject = { margin: "8px 0 4px", color: COLORS.text };
const headerMeta = { display: "flex", alignItems: "center", gap: 10, color: COLORS.subtext, fontSize: 12 };
const quickActions = { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" };
const contentGrid = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr)",
  minHeight: 0,
  gap: 12,
  padding: 12,
};
const chatPane = {
  display: "flex",
  flexDirection: "column",
  minHeight: 520,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  overflow: "hidden",
};
const messagesWrap = { flex: 1, overflowY: "auto", padding: 14, background: "#fff" };
const messageRow = { display: "flex", marginBottom: 12 };
const messageAuthor = { color: COLORS.subtext, fontSize: 11, marginBottom: 4 };
const userBubble = {
  display: "inline-block",
  padding: "9px 12px",
  borderRadius: 12,
  background: "#f1f5f9",
  color: COLORS.text,
};
const agentBubble = {
  ...userBubble,
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
};
const messageTime = { color: COLORS.subtext, fontSize: 10, marginTop: 4 };
const composer = { display: "flex", gap: 8, padding: 12, borderTop: `1px solid ${COLORS.border}` };
const composerInput = {
  flex: 1,
  minWidth: 0,
  padding: "10px 12px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  outline: "none",
};
const sidePane = {
  overflowY: "visible",
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: 12,
  alignContent: "start",
};
const panel = { border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden", background: "#fff" };
const panelTitle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  borderBottom: `1px solid ${COLORS.border}`,
  background: "#f8fafc",
};
const panelIcon = { color: COLORS.primary, display: "grid", placeItems: "center" };
const panelBody = { padding: 12, display: "grid", gap: 10 };
const label = { color: COLORS.subtext, fontSize: 12, fontWeight: 800 };
const inlineControls = { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 40px", gap: 8 };
const roleWrap = { display: "flex", flexWrap: "wrap", gap: 8 };
const roleChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 9px",
  borderRadius: 999,
  background: "#f8fafc",
  border: `1px solid ${COLORS.border}`,
  fontSize: 12,
};
const roleChipSmall = { ...roleChip, padding: "4px 7px", fontSize: 11 };
const agentChipSmall = { ...roleChipSmall, background: "#eff6ff", color: COLORS.info };
const ghostChip = {
  display: "inline-flex",
  alignItems: "center",
  padding: "5px 8px",
  borderRadius: 999,
  border: `1px dashed ${COLORS.border}`,
  color: COLORS.subtext,
  fontSize: 11,
};
const chipClose = {
  border: "none",
  background: "transparent",
  display: "grid",
  placeItems: "center",
  color: COLORS.subtext,
  cursor: "pointer",
};
const detailList = { display: "grid", gap: 8 };
const detailRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: 10,
  padding: "8px 0",
  borderBottom: `1px solid ${COLORS.border}`,
  fontSize: 12,
};
const emptyNote = { color: COLORS.subtext, background: "#f8fafc", border: `1px solid ${COLORS.border}`, padding: 12, borderRadius: 10 };
const baseBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  minHeight: 38,
  padding: "0 12px",
  borderRadius: 10,
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  fontWeight: 800,
  cursor: "pointer",
};
const ghostBtn = { ...baseBtn };
const fullGhostBtn = { ...baseBtn, width: "100%" };
const iconBtn = { ...baseBtn, padding: 0, width: 40 };
const dangerBtn = { ...baseBtn, color: COLORS.danger, borderColor: "#fecaca", background: "#fef2f2" };
const sendBtn = {
  ...baseBtn,
  border: "none",
  color: "#fff",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
};
const denied = { padding: 24, color: COLORS.danger, fontWeight: 800 };
